import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  ReactNode,
  FC,
} from "react";
import Loader from "../templates/Loader/Loader";

interface QueryParams {
  [key: string]: string;
}

interface PathState {
  defaultPagePath: boolean;
  newTpath: boolean;
  newT2path: boolean;
  newHpath: boolean;
  newH2path: boolean;
  newH2quizPath: boolean;
  newSpath: boolean;
  newSquizPath: boolean;
  iqTestPath: boolean;
  applyDiscountPage: boolean;
  checkoutPagePath: boolean;
  holidayPromoPath: boolean;
  holidayPromo23dPath: boolean;
  holidayPromo3Path: boolean;
  learningDeskOnePath: boolean;
  w2aReadingPath: boolean;
  w2aLessonsPath: boolean;
  w2aMindPalacePath: boolean;
}

export type CurrentFlow =
  | "new-t"
  | "new-t2"
  | "new-h"
  | "new-h2"
  | "new-s"
  | "iq-test"
  | "apply-discount"
  | "checkout"
  | "hp"
  | "hp-23d"
  | "hp-3"
  | "learning-desk-one"
  | "w2a-reading"
  | "w2a-lessons"
  | "w2a-mind-palace"
  | null;

interface UrlContextValue {
  queryParams: QueryParams;
  getCurrentPath: () => Promise<string>;
  currentFlow: CurrentFlow;
  isGoogleAdsTraffic: boolean;
  isMetaAdsTraffic: boolean;
  defaultPagePath: boolean;
  applyDiscountPage: boolean;
  newTpath: boolean;
  newT2path: boolean;
  newHpath: boolean;
  iqTestPath: boolean;
  newH2path: boolean;
  newH2quizPath: boolean;
  newSpath: boolean;
  newSquizPath: boolean;
  checkoutPagePath: boolean;
  validRegularPathsCheck: boolean;
  holidayPromoPath: boolean;
  holidayPromo23dPath: boolean;
  holidayPromo3Path: boolean;
  learningDeskOnePath: boolean;
  w2aReadingPath: boolean;
  w2aLessonsPath: boolean;
  w2aMindPalacePath: boolean;
  w2aPath: boolean;
  validHolidayPathsCheck: boolean;
}

interface UrlProviderProps {
  children: ReactNode;
}

const UrlContext = createContext<UrlContextValue | undefined>(undefined);

export const useUrlContext = (): UrlContextValue => {
  const context = useContext(UrlContext);
  if (context === undefined) {
    throw new Error('useUrlContext must be used within a UrlProvider');
  }
  return context;
};

export const UrlProvider: FC<UrlProviderProps> = ({ children }) => {
  const [queryParams, setQueryParams] = useState<QueryParams>({});
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  const initialPathState: PathState = {
    defaultPagePath: false,
    newTpath: false,
    newT2path: false,
    newHpath: false,
    newH2path: false,
    newH2quizPath: false,
    newSpath: false,
    newSquizPath: false,
    iqTestPath: false,
    applyDiscountPage: false,
    checkoutPagePath: false,
    holidayPromoPath: false,
    holidayPromo23dPath: false,
    holidayPromo3Path: false,
    learningDeskOnePath: false,
    w2aReadingPath: false,
    w2aLessonsPath: false,
    w2aMindPalacePath: false
  };

  const [pathState, setPathState] = useState<PathState>(initialPathState);

  const getCurrentPath = async (): Promise<string> => {
    return window.location.href;
  };

  useEffect(() => {
    const initializeContext = async () => {
      const storedParams: QueryParams =
        JSON.parse(localStorage.getItem("MemoryOSQueryParams") || "{}");
      const params = getQueryParameters();

      if (Object.keys(params).length > 0) {
        setQueryParams(params);
        localStorage.setItem(
          "MemoryOSQueryParams",
          JSON.stringify(params)
        );
      } else if (Object.keys(storedParams).length > 0) {
        setQueryParams(storedParams);
      }

      setIsInitialized(true);
    };

    initializeContext();
  }, []);

  const getQueryParameters = (): QueryParams => {
    const urlParams = new URLSearchParams(window.location.search);
    const dataUrl: QueryParams = {};

    urlParams.forEach((value, key) => {
      dataUrl[key] = value;
    });

    return dataUrl;
  };

  const path = window.location.pathname;

  useEffect(() => {
    if (path) {
      const defaultPath = /^\/($|\?)|(\/new-h2)(\/|$|\?|#)/.test(path);
      const hasNewHpath = /\/new-h(\/|$|\?|#)/.test(path);
      const hasNewH2path = /\/new-h2(\/|$|\?|#)/.test(path);
      const hasNewH2quizPath = /\/new-h2\/quiz\/[^/?#]+(\/|$|\?|#)/.test(path);
      const hasNewSpath = /\/new-s(\/|$|\?|#)/.test(path);
      const hasNewSquizPath = /\/new-s\/quiz\/[^/?#]+(\/|$|\?|#)/.test(path);
      const hasNewTpath = /\/new-t(\/|$|\?|#)/.test(path);
      const hasNewT2path = /\/new-t2(\/|$|\?|#)/.test(path);
      const hasIqTestPath = /\/iq-test(\/|$|\?|#)/.test(path);
      const hasApplyDiscountPath = /\/apply-discount(\/|$|\?|#)/.test(path);
      const hasCheckoutPage = /\/checkout(\/|$|\?|#)/.test(path);
      const hasHPpath = /\/hp(\/|$|\?|#)/.test(path);
      const hasHP23dpath = /\/hp-23d(\/|$|\?|#)/.test(path);
      const hasHP3path = /\/hp-3(\/|$|\?|#)/.test(path);
      const hasLearningDeskOnePath = /\/learning-desk-1(\/|$|\?|#)/.test(path);
      // The bare /w2a redirects to reading; the flags are read once per page load, so it must already count as reading.
      const hasW2aReadingPath = /\/w2a\/reading(\/|$|\?|#)/.test(path) || /^\/w2a\/?$/.test(path);
      const hasW2aLessonsPath = /\/w2a\/lessons(\/|$|\?|#)/.test(path);
      const hasW2aMindPalacePath = /\/w2a\/mind-palace(\/|$|\?|#)/.test(path);

      if (hasApplyDiscountPath) {
        localStorage.setItem("MemoryOSDownSale", "false");
      }

      setPathState({
        defaultPagePath: defaultPath,
        applyDiscountPage: hasApplyDiscountPath,
        newTpath: hasNewTpath,
        newT2path: hasNewT2path,
        newHpath: hasNewHpath,
        newH2path: hasNewH2path,
        newH2quizPath: hasNewH2quizPath,
        newSpath: hasNewSpath,
        newSquizPath: hasNewSquizPath,
        iqTestPath: hasIqTestPath,
        checkoutPagePath: hasCheckoutPage,
        holidayPromoPath: hasHPpath,
        holidayPromo23dPath: hasHP23dpath,
        holidayPromo3Path: hasHP3path,
        learningDeskOnePath: hasLearningDeskOnePath,
        w2aReadingPath: hasW2aReadingPath,
        w2aLessonsPath: hasW2aLessonsPath,
        w2aMindPalacePath: hasW2aMindPalacePath
      });
    }
  }, [path]);

  const validRegularPathsCheck = useMemo(() => {
    return Boolean(
      pathState.defaultPagePath ||
      pathState.applyDiscountPage ||
      pathState.newTpath ||
      pathState.newT2path ||
      pathState.newHpath ||
      pathState.iqTestPath ||
      pathState.newH2path ||
      pathState.newSpath
    );
  }, [
    pathState.applyDiscountPage,
    pathState.defaultPagePath,
    pathState.newTpath,
    pathState.newT2path,
    pathState.newHpath,
    pathState.iqTestPath,
    pathState.newH2path,
    pathState.newSpath]);

  const validHolidayPathsCheck = useMemo(() => {
    return Boolean(
      pathState.holidayPromoPath ||
      pathState.holidayPromo23dPath ||
      pathState.holidayPromo3Path
    );
  }, [
    pathState.holidayPromoPath,
    pathState.holidayPromo23dPath,
    pathState.holidayPromo3Path,
  ]);

  const isGoogleAdsTraffic = useMemo(() => {
    return queryParams?.utm_source === "googleads";
  }, [queryParams]);

  const isMetaAdsTraffic = useMemo(() => {
    const source = queryParams?.utm_source;
    return source === "facebookads" || source === "facebook";
  }, [queryParams]);

  const currentFlow = useMemo<CurrentFlow>(() => {
    if (pathState.defaultPagePath) return "new-h2";
    if (pathState.newTpath) return "new-t";
    if (pathState.newT2path) return "new-t2";
    if (pathState.newHpath) return "new-h";
    if (pathState.newH2path) return "new-h2";
    if (pathState.newSpath) return "new-s";
    if (pathState.iqTestPath) return "iq-test";
    if (pathState.applyDiscountPage) return "apply-discount";
    if (pathState.checkoutPagePath) return "checkout";
    if (pathState.holidayPromoPath) return "hp";
    if (pathState.holidayPromo23dPath) return "hp-23d";
    if (pathState.holidayPromo3Path) return "hp-3";
    if (pathState.learningDeskOnePath) return "learning-desk-one";
    if (pathState.w2aReadingPath) return "w2a-reading";
    if (pathState.w2aLessonsPath) return "w2a-lessons";
    if (pathState.w2aMindPalacePath) return "w2a-mind-palace";

    return null;
  }, [pathState]);

  const w2aPath =
    pathState.w2aReadingPath ||
    pathState.w2aLessonsPath ||
    pathState.w2aMindPalacePath;

  const contextValue: UrlContextValue = {
    queryParams,
    getCurrentPath,
    currentFlow,
    isGoogleAdsTraffic,
    isMetaAdsTraffic,
    defaultPagePath: pathState.defaultPagePath,
    applyDiscountPage: pathState.applyDiscountPage,
    newTpath: pathState.newTpath,
    newT2path: pathState.newT2path,
    newHpath: pathState.newHpath,
    newH2path: pathState.newH2path,
    newH2quizPath: pathState.newH2quizPath,
    newSpath: pathState.newSpath,
    newSquizPath: pathState.newSquizPath,
    iqTestPath: pathState.iqTestPath,
    checkoutPagePath: pathState.checkoutPagePath,
    validRegularPathsCheck,
    holidayPromoPath: pathState.holidayPromoPath,
    holidayPromo23dPath: pathState.holidayPromo23dPath,
    holidayPromo3Path: pathState.holidayPromo3Path,
    learningDeskOnePath: pathState.learningDeskOnePath,
    w2aReadingPath: pathState.w2aReadingPath,
    w2aLessonsPath: pathState.w2aLessonsPath,
    w2aMindPalacePath: pathState.w2aMindPalacePath,
    w2aPath,
    validHolidayPathsCheck
  };

  if (!isInitialized) {
    return <Loader />;
  }

  return (
    <UrlContext.Provider value={contextValue}>{children}</UrlContext.Provider>
  );
};
